// RegisterMerchant.js
import React, { useMemo, useState, useEffect } from 'react';
import './RegisterMerchant.css';
import { useNavigate } from 'react-router-dom';
import { uploadToSpaces } from './utils/uploadToSpaces';
import { compressImage } from './utils/compressImage';
import api from './api';
import placeholderLogo from './assets/placeholder-logo.png';
import placeholderBanner from './assets/placeholder-banner.png';

// ✅ NEW: merchant wallet hard-block popup
import MerchantWalletRequiredPrompt from './components/popups/MerchantWalletRequiredPrompt';

// ✅ ISO codes → country names
import { ISO_COUNTRY_CODES } from './constants/countryCodes';

const sanitizeLocationInput = (value) => {
  return value.replace(/[^\p{L}\p{N}\s,-]/gu, '');
};

// 🧼 Remove emojis from business name
const sanitizeBusinessName = (value) => {
  if (!value) return '';
  const str = value.toString();
  return str.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '');
};

function RegisterMerchant({ user }) {
  const navigate = useNavigate();

  // ✅ draft key (per user)
  const DRAFT_KEY = `bf:merchantRegisterDraft:${user?._id || 'anon'}`;

  const [businessUsername, setBusinessUsername] = useState('');
  const [businessName, setBusinessName] = useState('');

  const [countryCode, setCountryCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const [customCountry, setCustomCountry] = useState('');

  const [description, setDescription] = useState('');
  const [businessLogo, setBusinessLogo] = useState('');
  const [bannerImage, setBannerImage] = useState('');

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);

  // ✅ NEW: wallet gate state
  const [showWalletGate, setShowWalletGate] = useState(false);
  const [checkingWallet, setCheckingWallet] = useState(false);

  const categories = ['Barter', 'Physical Goods', 'Digital Goods', 'Services'];

  const countries = useMemo(() => {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    const list = ISO_COUNTRY_CODES
      .map(code => ({ code, name: dn.of(code) || code }))
      .filter(c => c.name && c.name.length > 1)
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...list, { code: 'OTHER', name: 'Other' }];
  }, []);

  // ✅ Host/network logic (same as your other screens)
  const host = window.location.hostname || '';
  const isTestnetHost = host.includes('testnet.');
  const isMainnetHost = !isTestnetHost && host.includes('barterfi.app');

  const MIN_MAINNET_ACTIVATION_PI = Number(
    process.env.REACT_APP_MIN_MAINNET_ACTIVATION_PI || 0.1
  );

  // ✅ Restore draft on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);

      setBusinessUsername(d.businessUsername || '');
      setBusinessName(d.businessName || '');

      setCountryCode(d.countryCode || '');
      setCountryName(d.countryName || '');
      setCustomCountry(d.customCountry || '');

      setDescription(d.description || '');
      setBusinessLogo(d.businessLogo || '');
      setBannerImage(d.bannerImage || '');

      setSelectedCategories(Array.isArray(d.selectedCategories) ? d.selectedCategories : []);
      setTermsAccepted(Boolean(d.termsAccepted));
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only once

  // ✅ Save draft whenever fields change
  useEffect(() => {
    const draft = {
      businessUsername,
      businessName,
      countryCode,
      countryName,
      customCountry,
      description,
      businessLogo,
      bannerImage,
      selectedCategories,
      termsAccepted,
    };

    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // ignore
    }
  }, [
    DRAFT_KEY,
    businessUsername,
    businessName,
    countryCode,
    countryName,
    customCountry,
    description,
    businessLogo,
    bannerImage,
    selectedCategories,
    termsAccepted,
  ]);

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLogoLoading(true);
      const compressed = await compressImage(file);
      const url = await uploadToSpaces(compressed, 'business-logos');
      setBusinessLogo(url);
    } catch {
      showError('Failed to upload logo. Please try again.');
    } finally {
      setLogoLoading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setBannerLoading(true);
      const compressed = await compressImage(file, 0.8);
      const url = await uploadToSpaces(compressed, 'merchant-banners');
      setBannerImage(url);
    } catch {
      showError('Failed to upload banner. Please try again.');
    } finally {
      setBannerLoading(false);
    }
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 2500);
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const resolveLocationString = () => {
    if (countryCode === 'OTHER') {
      return sanitizeLocationInput(customCountry).trim();
    }
    return countryName || '';
  };

  // ✅ NEW: wallet activation hard check (run ONLY when user clicks Register)
  const checkWalletActivationBeforeSubmit = async () => {
    // Enforce only on mainnet host (same behavior as your app)
    if (!isMainnetHost) return true;

    // use passed prop user first, fallback to localStorage
    const stored = (() => {
      try { return JSON.parse(localStorage.getItem('barterfi_user')); } catch { return null; }
    })();

    const username = user?.username || stored?.username;
    if (!username) return true; // if no username, don't block here

    try {
      setCheckingWallet(true);

      const res = await api.get(`/wallet/${username}?network=mainnet`);
      const history = res.data?.history || [];

      const depositTotal = history
        .filter((tx) => ['deposit', 'topup'].includes(tx.type))
        .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

      const inactive = depositTotal < MIN_MAINNET_ACTIVATION_PI;

      if (inactive) {
        setShowWalletGate(true);
        return false;
      }

      return true;
    } catch (err) {
      console.warn('[RegisterMerchant] wallet activation check failed:', err);
      // safest: block if we can't confirm activation
      setShowWalletGate(true);
      return false;
    } finally {
      setCheckingWallet(false);
    }
  };

  const handleSubmit = async () => {
    // ✅ Wallet gate FIRST (hard block)
    const ok = await checkWalletActivationBeforeSubmit();
    if (!ok) return;

    // ✅ normal validations
    if (!businessUsername.trim()) return showError('Business username is required.');
    if (!businessName.trim()) return showError('Business name is required.');
    if (!countryCode) return showError('Please select your country.');
    if (countryCode === 'OTHER' && !customCountry.trim()) {
      return showError('Please type your country name.');
    }
    if (selectedCategories.length === 0) {
      return showError('Select at least one business type.');
    }
    if (!termsAccepted) {
      return showError('Accept the Terms and Conditions.');
    }

    setLoading(true);

    const payload = {
      userId: user._id,
      username: user.username,
      businessUsername,
      businessName,
      location: resolveLocationString(),
      countryCode: countryCode === 'OTHER' ? null : countryCode,
      countryName: countryCode === 'OTHER' ? resolveLocationString() : countryName,
      description,
      businessLogo,
      bannerImage,
      categories: selectedCategories,
      userType: 'merchant',
    };

    try {
      const res = await api.post('/users/register-merchant', payload);
      if (res.status === 200 || res.status === 201) {
        // ✅ Clear draft after success
        try { sessionStorage.removeItem(DRAFT_KEY); } catch {}

        showSuccess('Application submitted. Awaiting admin approval.');
        setTimeout(() => (window.location.href = '/'), 1500);
      } else {
        showError(res.data.message || 'Registration failed.');
        setLoading(false);
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Network error.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
    navigate(-1);
  };

  return (
    <>
      {loading && (
        <div className="register-merchant-container">
          <div className="merchant-loading-overlay">
            <div className="merchant-loading-box">
              <div className="loader" />
              <p className="loader-text">Submitting Application...</p>
            </div>
          </div>
        </div>
      )}

      <div className="register-merchant-container">
        {error && <div className="merchant-banner error">{error}</div>}
        {success && <div className="merchant-banner success">{success}</div>}

        <h2>Register as Merchant</h2>

        <div className={`content ${error || success ? 'has-banner' : ''}`}>
          <label>Business Name <span className="req">*</span></label>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(sanitizeBusinessName(e.target.value))}
            placeholder="Enter your business name"
            required
          />

          <label>Business Username <span className="req">*</span></label>
          <input
            type="text"
            value={businessUsername}
            onChange={(e) => {
              const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
              setBusinessUsername(sanitized);
            }}
            placeholder="e.g. test_store01"
            required
          />

          <label>Country <span className="req">*</span></label>
          <select
            value={countryCode}
            onChange={(e) => {
              const code = e.target.value;
              setCountryCode(code);
              if (code === 'OTHER') {
                setCountryName('');
              } else {
                const found = countries.find(c => c.code === code);
                setCountryName(found?.name || '');
                setCustomCountry('');
              }
            }}
            required
          >
            <option value="" disabled>Select your country</option>
            {countries.map(({ code, name }) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>

          {countryCode === 'OTHER' && (
            <>
              <label>Your Country <span className="req">*</span></label>
              <input
                type="text"
                value={customCountry}
                onChange={(e) => setCustomCountry(sanitizeLocationInput(e.target.value))}
                placeholder="Type your country"
                required
              />
            </>
          )}

          <label>Store Description (Optional)</label>
          <textarea
            rows="4"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <label>Business Logo (Optional)</label>
          <input type="file" accept="image/*" onChange={handleLogoUpload} />
          {logoLoading && <div className="spinner-preview" />}
          <img
            src={businessLogo || placeholderLogo}
            alt="Logo preview"
            className="logo-preview"
          />

          <label>Banner Image (Optional)</label>
          <input type="file" accept="image/*" onChange={handleBannerUpload} />
          {bannerLoading && <div className="spinner-preview" />}
          <img
            src={bannerImage || placeholderBanner}
            alt="Banner preview"
            className="banner-preview"
          />

          <p>Select your business type(s):</p>
          <div className="category-options">
            {categories.map((cat) => (
              <label key={cat}>
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(cat)}
                  onChange={() => handleCategoryToggle(cat)}
                />
                {cat}
              </label>
            ))}
          </div>

          <div className="terms-section">
            <label className="terms-check">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={() => setTermsAccepted((v) => !v)}
              />
              I agree to BarterFi&apos;s Merchant Terms & Conditions
            </label>

            <button
              type="button"
              className="terms-link"
              onClick={() => navigate('/merchant-terms')}
            >
              View Terms
            </button>
          </div>
        </div>

        <div className="button-group">
          <button onClick={handleCancel}>Cancel</button>

          <button
            onClick={handleSubmit}
            disabled={loading || checkingWallet}
          >
            {checkingWallet ? 'Checking...' : 'Register'}
          </button>
        </div>
      </div>

      {/* ✅ Wallet gate popup (blocks submission) */}
      {showWalletGate && (
        <MerchantWalletRequiredPrompt
          threshold={MIN_MAINNET_ACTIVATION_PI}
          onClose={() => setShowWalletGate(false)}
          onActivate={() => {
            setShowWalletGate(false);
            navigate('/my-wallet');
          }}
        />
      )}
    </>
  );
}

export default RegisterMerchant;
